import { useEffect, useId, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { HelpTip } from '../components/HelpTip';
import { VerInstructivo } from '../components/VerInstructivo';
import { useToast } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { api, extractErrorMessage } from '../api/client';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useAvisoImpactoMl } from '../hooks/useAvisoImpactoMl';
import { useCamposObligatorios, faltaSi } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from '../components/Obligatorio';
import { BuscadorSku, type SkuSugerido } from '../components/BuscadorSku';
import { elegirPorCodigo, recordarIdentidad, useFilasConIdentidad, type Resolucion } from '../lib/identidadArticulos';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { ChipsVariante } from '../components/ChipsVariante';
import type { VariantesDeSku } from '../lib/variantes';
import { TIPO_MOVIMIENTO_LABEL, type TipoMovimiento } from '../lib/tiposMovimiento';
import {
  A_CARGO_DE, A_CARGO_DE_LABEL, CAMPOS_QUE_PIDE, ETIQUETA_SIN_MOTIVO, URL_MOTIVOS, comoLista, etiquetaACargoDe,
  type MotivoMovimiento,
} from '../lib/motivosMovimiento';
import { referenciaLegible, type OrigenReferencia } from '../lib/referenciaMovimiento';
import { BajasAltasPorMotivo } from './movimientos-stock/BajasAltasPorMotivo';
import {
  MAX_DIAS_ATRAS_HECHO, ahoraLocalAr, fechaHoraAr, localArAIso, minHechoLocalAr, mismoMinuto,
} from '../lib/fechaHoraAr';

interface MovimientoStock {
  id: number;
  skuInterno: string;
  ubicacionId: number | null;
  ubicacionCodigo: string | null;
  tipo: TipoMovimiento;
  cantidad: number;
  cantidadAnterior: number | null;
  cantidadPosterior: number | null;
  motivo: string | null;
  referencia: string | null;
  usuario: string | null;
  /** Quién lo hizo: el usuario, "Sistema (Mercado Libre)" o "Sistema" (backend: operadorDe). */
  operador?: string;
  /** Motivo del catálogo (null: sin motivo del catálogo) y a cargo de quién. */
  motivoId?: number | null;
  motivoNombre?: string | null;
  aCargoDe?: string | null;
  /** Manual sin motivo del catálogo (anterior a que fuera obligatorio): se muestra ETIQUETA_SIN_MOTIVO. */
  historico?: boolean;
  /** Datos que pidió el motivo (null: no se cargaron). */
  numeroVenta?: string | null;
  publicacion?: string | null;
  contacto?: string | null;
  /** Qué proceso escribió la referencia y su número (null: manual). */
  origenReferencia?: OrigenReferencia | null;
  /** Cuándo se registró. */
  createdAt: string;
  /** Cuándo pasó, si no fue al registrarlo. */
  ocurridoEn?: string | null;
  /** Momento efectivo: ocurridoEn ?? createdAt (el orden de la lista). */
  momento?: string;
  /** Variante (color) que movió, si se sabe (remitos, ventas y ajustes con variante); null: Sin asignar o anterior a las variantes. */
  varianteId?: number | null;
  varianteNombre?: string | null;
}

interface RespuestaMovimientos {
  data: MovimientoStock[];
  total: number;
  totales: { entradas: number; salidas: number; neto: number };
  /** Desde cuándo se listó: el filtro o el comienzo de la ventana por defecto. */
  desde?: string;
}

interface Ubicacion { id: number; codigo: string; }

const TIPO_LABEL = TIPO_MOVIMIENTO_LABEL;

const TIPO_TONO: Record<TipoMovimiento, 'green' | 'red' | 'blue'> = {
  INGRESO_COMPRA: 'green',
  DEVOLUCION_CLIENTE: 'green',
  AJUSTE_POSITIVO: 'green',
  SALIDA_VENTA: 'red',
  BAJA_ROTURA: 'red',
  BAJA_VENCIMIENTO: 'red',
  DEVOLUCION_PROVEEDOR: 'red',
  AJUSTE_NEGATIVO: 'red',
  TRANSFERENCIA: 'blue',
};

/** Tipos manuales que suman stock (el resto de los que registra un motivo, descuentan). */
const TIPOS_ENTRADA_MANUAL: TipoMovimiento[] = ['DEVOLUCION_CLIENTE', 'AJUSTE_POSITIVO'];

const fmtCantidad = (n: number | null) =>
  n === null ? '—' : Number(n).toLocaleString('es-AR', { maximumFractionDigits: 4 });

/** Aviso de una carga que falló en el formulario, con el botón para volver a pedirla. */
function AvisoCarga({ mensaje, onReintentar }: { mensaje: string; onReintentar: () => void }) {
  return (
    <div role="alert" className="mt-1 flex items-center gap-2 text-xs text-red-600">
      <span>{mensaje}</span>
      <button type="button" onClick={onReintentar} className="underline font-medium">Reintentar</button>
    </div>
  );
}

/** Momento efectivo: cuándo pasó (ocurridoEn) o, si no se indicó, cuándo se registró. */
const momentoDe = (m: MovimientoStock) => m.momento ?? m.ocurridoEn ?? m.createdAt;

/** Fecha y hora (Argentina) de cuándo se registró; en ámbar si se cargó en otro minuto que el del hecho. */
function RegistradoMovimiento({ m }: { m: MovimientoStock }) {
  const despues = !!m.ocurridoEn && !mismoMinuto(m.ocurridoEn, m.createdAt);
  return (
    <span className={`whitespace-nowrap ${despues ? 'text-amber-700' : ''}`} title={despues ? 'Se cargó después de que pasó' : undefined}>
      {fechaHoraAr(m.createdAt)}
    </span>
  );
}

/** Referencia legible ("Conteo #5"), con enlace si hay una pantalla para verla. */
function ReferenciaMovimiento({ m }: { m: MovimientoStock }) {
  const { texto, enlace } = referenciaLegible(m.referencia, m.origenReferencia);
  return enlace ? <Link to={enlace} className="text-brand-700 hover:underline">{texto}</Link> : <>{texto}</>;
}

/** Datos que pidió el motivo, en una línea ("Venta 2000001 · Publicación MLA123"); sin datos, null. */
const datosDelMotivo = (m: MovimientoStock) =>
  CAMPOS_QUE_PIDE.filter(d => m[d.campo]).map(d => `${d.corto} ${m[d.campo]}`).join(' · ') || null;

/** Quién hizo el movimiento (los viejos sin el campo: el usuario o "Sistema"). */
const operadorDe = (m: MovimientoStock) => m.operador ?? m.usuario ?? 'Sistema';

/** Sin "Desde" el backend lista los últimos 30 días (VENTANA_MOVIMIENTOS_DIAS en movimientos-stock.service.ts). */
const VENTANA_MOVIMIENTOS_DIAS = 30;

/**
 * Idempotency key for POST movimientos-stock. crypto.randomUUID only exists in secure
 * contexts (HTTPS/localhost), so fall back to a v4 UUID built from getRandomValues.
 */
const nuevoCommandId = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

/** SKU del catálogo desde movimientos-stock/skus: pide wms:ver (no catalogo:ver) y no trae costos ni precios. */
const URL_SKUS = '/api/v3/movimientos-stock/skus';
const buscarSkus = (texto: string) => api.get<SkuSugerido[]>(`${URL_SKUS}?q=${encodeURIComponent(texto)}`);
const resolverSkus = (codigo: string) => api.get<SkuSugerido[]>(`${URL_SKUS}?codigos=${encodeURIComponent(codigo)}`);
const MENSAJE_SKU_INEXISTENTE = 'El SKU no existe en el catálogo';

/** Formulario vacío. ubicacionId: la preseleccionada (con una sola ubicación) o vacío. */
const formVacio = (ubicacionId = '') => ({
  motivoId: '', detalle: '', referencia: '', numeroVenta: '', publicacion: '', contacto: '', skuInterno: '', cantidad: '',
  ocurridoEn: ahoraLocalAr(), ubicacionId,
});

/** Valor del filtro "Motivo" que pide los manuales sin motivo del catálogo (se muestra ETIQUETA_SIN_MOTIVO; el backend: sinCatalogar). */
const SIN_CATALOGAR = 'sin-catalogar';

type Vista = 'movimientos' | 'informe';

export function MovimientosStockPage() {
  const toast = useToast();
  const avisarImpactoMl = useAvisoImpactoMl();
  const { can } = useAuth();
  const puedeRegistrar = can('wms', 'editar');

  const [vista, setVista] = useState<Vista>('movimientos');
  const [sku, setSku] = useState('');
  const [tipo, setTipo] = useState('');
  const [motivoFiltro, setMotivoFiltro] = useState('');
  const [aCargoDeFiltro, setACargoDeFiltro] = useState('');
  const [operadorFiltro, setOperadorFiltro] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const skuDebounced = useDebounce(sku, 400);

  const params = new URLSearchParams();
  params.set('limit', String(pageSize));
  params.set('offset', String((page - 1) * pageSize));
  if (skuDebounced.trim()) params.set('sku', skuDebounced.trim());
  if (tipo) params.set('tipo', tipo);
  if (desde) params.set('desde', desde);
  if (hasta) params.set('hasta', hasta);
  if (motivoFiltro === SIN_CATALOGAR) params.set('sinCatalogar', 'true');
  else if (motivoFiltro) params.set('motivoId', motivoFiltro);
  if (aCargoDeFiltro) params.set('aCargoDe', aCargoDeFiltro);
  if (operadorFiltro) params.set('operador', operadorFiltro);
  const endpoint = `/api/v3/movimientos-stock?${params.toString()}`;

  // Catálogo de motivos (todos: el filtro ofrece también los desactivados; el formulario, sólo los activos).
  // Operadores: usuarios con algún movimiento.
  const { data: motivosResp, error: errorMotivos, refetch: recargarMotivos } = useApi<MotivoMovimiento[]>(`${URL_MOTIVOS}?incluirInactivos=true`);
  const { data: operadoresResp } = useApi<Array<{ id: number; username: string }>>('/api/v3/movimientos-stock/operadores');
  const motivos = comoLista<MotivoMovimiento>(motivosResp);
  const motivosActivos = motivos.filter(m => m.activo);
  const operadores = comoLista<{ id: number; username: string }>(operadoresResp);

  const { data: resp, loading, error, refetch } = useApi<RespuestaMovimientos>(endpoint);
  const { data: ubicaciones, error: errorUbicaciones, refetch: recargarUbicaciones } = useApi<Ubicacion[]>('/api/v3/ubicaciones?limit=500&offset=0');
  const listaUbicaciones = comoLista<Ubicacion>(ubicaciones);
  // Con una sola ubicación (hoy DEPOSITO-GENERAL) se preselecciona al abrir; con varias, se elige.
  const ubicacionPorDefecto = listaUbicaciones.length === 1 ? String(listaUbicaciones[0].id) : '';

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(formVacio());
  const [formError, setFormError] = useState('');
  const cierreAlta = useCierreConfirmado(useHayCambios(form, open), () => setOpen(false));
  // Obligatorios (backend: RegistrarMovimientoStockDto): SKU, ubicación, cantidad (mayor a 0) y motivo. SKU y motivo se
  // mandan recortados: con sólo espacios faltan.
  const obligatorios = useCamposObligatorios(open);
  const [submitting, setSubmitting] = useState(false);
  // One per modal open: a double submit or a retry of the same registro is deduplicated by the backend.
  const [commandId, setCommandId] = useState('');
  // "Ahora" al abrir: si no se cambia, el hecho es cuando se registra y no se manda.
  const [ocurridoInicial, setOcurridoInicial] = useState('');

  // The typed code, resolved to an article (the field stores its skuInterno). Checked on blur and before saving with the
  // exact code lookup; one request per text while the modal is open. Picking from the list needs no request. A code of
  // several suppliers' articles, or none, is flagged under the field and stops the save.
  const idSku = useId();
  const [skuVerificado, setSkuVerificado] = useState<{ sku: string; error: string } | null>(null);
  const verificaciones = useRef(new Map<string, Promise<Resolucion | null>>());
  const errorSku = skuVerificado && skuVerificado.error && skuVerificado.sku === form.skuInterno.trim() ? skuVerificado.error : '';

  /** The article the text names (unico), or why not (ambiguo / ninguno); null = could not check: the backend decides. */
  const verificarSku = (sku: string): Promise<Resolucion | null> => {
    // The exact lookup splits on commas: such a SKU is left to the backend.
    if (!sku || sku.includes(',')) return Promise.resolve(null);
    let pendiente = verificaciones.current.get(sku);
    if (!pendiente) {
      pendiente = resolverSkus(sku).then(
        r => elegirPorCodigo(r, sku),
        () => { verificaciones.current.delete(sku); return null; },
      );
      verificaciones.current.set(sku, pendiente);
    }
    return pendiente.then(r => {
      if (r?.tipo === 'unico') {
        const a = r.articulo;
        recordarIdentidad(a);
        verificaciones.current.set(a.skuInterno, Promise.resolve(r));
        setForm(f => (f.skuInterno.trim() === sku ? { ...f, skuInterno: a.skuInterno } : f));
        setSkuVerificado({ sku: a.skuInterno, error: '' });
        cargarVariantes(a.skuInterno);
      } else if (r) {
        setSkuVerificado({ sku, error: r.tipo === 'ambiguo' ? r.mensaje : MENSAJE_SKU_INEXISTENTE });
      }
      return r;
    });
  };

  // Variantes del SKU del ajuste: sólo se muestran los chips si tiene. Se piden al elegir el SKU o al salir del campo.
  const [variantes, setVariantes] = useState<VariantesDeSku | null>(null);
  const [varianteId, setVarianteId] = useState<number | null>(null);
  const skuVariantes = useRef('');
  const cargarVariantes = (sku: string) => {
    if (!sku || skuVariantes.current === sku) return;
    skuVariantes.current = sku;
    api.get<VariantesDeSku>(`/api/v3/catalogo/${encodeURIComponent(sku)}/variantes`)
      .then(v => { if (skuVariantes.current === sku && v?.variantes?.length) setVariantes(v); })
      .catch(() => { /* sin variantes visibles: el ajuste sigue igual */ });
  };
  const limpiarVariantes = () => { skuVariantes.current = ''; setVariantes(null); setVarianteId(null); };

  // Reasignar variante: mueve unidades entre variantes de un SKU (o desde/hacia Sin asignar) sin cambiar el total.
  const [reasignando, setReasignando] = useState(false);
  const [reSku, setReSku] = useState('');
  const [reCantidad, setReCantidad] = useState('');
  const [reVariantes, setReVariantes] = useState<VariantesDeSku | null>(null);
  const [reDesde, setReDesde] = useState<number | null>(null);
  const [reHacia, setReHacia] = useState<number | null>(null);
  const [reError, setReError] = useState('');
  const [reEnviando, setReEnviando] = useState(false);
  // SKU elegido en la lista: una respuesta de variantes de otro SKU (llegó tarde) se descarta.
  const reSkuElegido = useRef('');
  const abrirReasignar = () => {
    setReSku(''); setReCantidad(''); setReVariantes(null); setReDesde(null); setReHacia(null); setReError(''); setReasignando(true);
    reSkuElegido.current = '';
  };
  const elegirSkuReasignar = (s: SkuSugerido) => {
    recordarIdentidad(s);
    reSkuElegido.current = s.skuInterno;
    setReSku(s.skuInterno); setReVariantes(null); setReDesde(null); setReHacia(null); setReError('');
    api.get<VariantesDeSku>(`/api/v3/catalogo/${encodeURIComponent(s.skuInterno)}/variantes`)
      .then(v => { if (reSkuElegido.current === s.skuInterno) setReVariantes(v?.variantes?.length ? v : null); })
      .catch(e => { if (reSkuElegido.current === s.skuInterno) setReError(extractErrorMessage(e)); });
  };
  const reasignar = async () => {
    if (reEnviando || !reVariantes || reVariantes.skuInterno !== reSku.trim()) return;
    setReError(''); setReEnviando(true);
    try {
      const v = await api.post<VariantesDeSku>('/api/v3/movimientos-stock/reasignar-variante', {
        skuInterno: reVariantes.skuInterno, cantidad: Number(reCantidad), desdeVarianteId: reDesde, haciaVarianteId: reHacia,
      });
      toast.addToast('Reasignación registrada', 'success');
      setReVariantes(v); setReCantidad(''); setReDesde(null); setReHacia(null);
    } catch (e) {
      setReError(extractErrorMessage(e));
    } finally {
      setReEnviando(false);
    }
  };

  const elegirSku = (s: SkuSugerido) => {
    recordarIdentidad(s);
    setForm(f => ({ ...f, skuInterno: s.skuInterno }));
    cargarVariantes(s.skuInterno);
    verificaciones.current.set(s.skuInterno, Promise.resolve({ tipo: 'unico', articulo: s }));
    setSkuVerificado({ sku: s.skuInterno, error: '' });
  };

  const avisarSku = (mensaje: string) => {
    toast.addToast(mensaje, 'error');
    document.getElementById(idSku)?.focus();
  };

  const cambiarFiltro = (setter: (v: string) => void) => (v: string) => { setter(v); setPage(1); };

  const motivoElegido = motivosActivos.find(m => String(m.id) === form.motivoId) ?? null;
  // Sin catálogo o sin ubicaciones (falló la carga, o no hay motivos activos) no se puede registrar.
  const sinMotivosActivos = !errorMotivos && motivosResp != null && motivosActivos.length === 0;
  const noSePuedeRegistrar = !!errorMotivos || !!errorUbicaciones || sinMotivosActivos;
  // Stock físico de hoy del SKU en la ubicación: se pide cuando el SKU se sabe que existe (elegido o verificado al salir
  // del campo) y hay ubicación. Sin fila en esa ubicación es 0.
  const skuConfirmado = skuVerificado && !skuVerificado.error && skuVerificado.sku === form.skuInterno.trim() ? skuVerificado.sku : '';
  const claveStock = open && skuConfirmado && form.ubicacionId ? `${skuConfirmado}|${form.ubicacionId}` : '';
  const [stockHoy, setStockHoy] = useState<{ clave: string; cantidad: number | null } | null>(null);
  useEffect(() => {
    if (!claveStock) return;
    const [skuStock, ubicacionStock] = claveStock.split('|');
    let vigente = true;
    api.get<Array<{ skuInterno: string; ubicacionId: number; cantidadActual: string | number }>>(
      `/api/v3/stock?sku=${encodeURIComponent(skuStock)}&ubicacion_id=${ubicacionStock}&limit=1`,
    ).then(
      filas => {
        const fila = comoLista<{ skuInterno: string; ubicacionId: number; cantidadActual: string | number }>(filas)
          .find(f => f.skuInterno === skuStock && String(f.ubicacionId) === ubicacionStock);
        if (vigente) setStockHoy({ clave: claveStock, cantidad: fila ? Number(fila.cantidadActual) : 0 });
      },
      () => { if (vigente) setStockHoy({ clave: claveStock, cantidad: null }); },
    );
    return () => { vigente = false; };
  }, [claveStock]);
  const stockActual = claveStock && stockHoy?.clave === claveStock ? stockHoy : null;
  const cantidadNum = Number(form.cantidad);
  const hayCantidad = cantidadNum > 0;
  const sale = motivoElegido?.sentido === 'salida';
  const quedara = stockActual?.cantidad != null && motivoElegido && hayCantidad
    ? stockActual.cantidad + (sale ? -cantidadNum : cantidadNum) : null;
  // Una salida que deja la ubicación debajo de 0 el backend la rechaza (descontarStockTx: cantidadActual >= cantidad,
  // para todo tipo que no es de entrada): se avisa y no se deja registrar. Las entradas pueden quedar negativas.
  const noAlcanza = quedara !== null && quedara < 0 && !!motivoElegido && !TIPOS_ENTRADA_MANUAL.includes(motivoElegido.tipo);
  const textoBoton = motivoElegido && hayCantidad ? `${sale ? 'Dar de baja' : 'Sumar'} ${fmtCantidad(cantidadNum)} u.` : 'Registrar';

  const pideReferencia = !!motivoElegido?.pideReferencia;
  const pideDetalle = !!motivoElegido?.pideDetalle;
  // Número de venta, publicación y contacto: sólo si el motivo los pide (y entonces son obligatorios).
  const camposPedidos = CAMPOS_QUE_PIDE.filter(d => motivoElegido?.[d.marca]);

  const guardar = async () => {
    if (submitting) return;
    // Known unknown or ambiguous code: its own message instead of the generic "missing fields" one.
    if (errorSku) { avisarSku(errorSku); return; }
    if (!obligatorios.validar()) return;
    const cantidad = Number(form.cantidad);
    const ocurridoEn = form.ocurridoEn && form.ocurridoEn !== ocurridoInicial ? localArAIso(form.ocurridoEn) : null;
    setFormError('');
    setSubmitting(true);
    try {
      // Also covers a click on the button before the blur lookup answered.
      const verificado = await verificarSku(form.skuInterno.trim());
      if (verificado?.tipo === 'ninguno') { avisarSku(MENSAJE_SKU_INEXISTENTE); return; }
      if (verificado?.tipo === 'ambiguo') { avisarSku(verificado.mensaje); return; }
      const skuInterno = verificado?.tipo === 'unico' ? verificado.articulo.skuInterno : form.skuInterno.trim();
      await api.post('/api/v3/movimientos-stock', {
        skuInterno,
        ubicacionId: Number(form.ubicacionId),
        // El tipo sale del motivo (el backend lo valida igual); el detalle es opcional.
        motivoId: motivoElegido!.id,
        tipo: motivoElegido!.tipo,
        cantidad,
        ...(variantes && varianteId !== null ? { varianteId } : {}),
        ...(form.detalle.trim() ? { detalle: form.detalle.trim() } : {}),
        ...(form.referencia.trim() ? { referencia: form.referencia.trim() } : {}),
        ...Object.fromEntries(camposPedidos.map(d => [d.campo, form[d.campo].trim()])),
        ...(ocurridoEn ? { ocurridoEn } : {}),
        commandId,
      });
      toast.addToast(`${TIPO_LABEL[motivoElegido!.tipo]} registrada`, 'success');
      avisarImpactoMl([skuInterno]);
      setOpen(false);
      setForm(formVacio(ubicacionPorDefecto));
      refetch();
    } catch (e) {
      setFormError(extractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'fechaHecho',
      header: 'Fecha del hecho',
      help: 'Fecha y hora (Argentina) en que pasó. Si no se indicó, es cuando se registró.',
      render: (m: MovimientoStock) => <span className="whitespace-nowrap">{fechaHoraAr(momentoDe(m))}</span>,
    },
    {
      key: 'registrado',
      header: 'Registrado',
      help: 'Fecha y hora (Argentina) en que se cargó en el sistema. En ámbar si se cargó después de que pasó.',
      render: (m: MovimientoStock) => <RegistradoMovimiento m={m} />,
    },
    {
      key: 'codigoArticulo',
      header: 'SKU',
      render: (m: MovimientoStock) => (
        <>
          <CodigoArticulo sku={m.skuInterno} />
          {m.varianteNombre && <span className="block text-xs text-slate-500" title="Variante">{m.varianteNombre}</span>}
        </>
      ),
    },
    { key: 'proveedorArticulo', header: 'Proveedor' },
    { key: 'ubicacionCodigo', header: 'Ubicación', render: (m: MovimientoStock) => m.ubicacionCodigo ?? '—' },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (m: MovimientoStock) => <Badge tone={TIPO_TONO[m.tipo]}>{TIPO_LABEL[m.tipo] ?? m.tipo}</Badge>,
    },
    {
      key: 'cantidad',
      header: 'Cantidad',
      align: 'right' as const,
      render: (m: MovimientoStock) => (
        <span className={m.cantidad >= 0 ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
          {m.cantidad > 0 ? '+' : ''}{fmtCantidad(m.cantidad)}
        </span>
      ),
    },
    {
      key: 'cantidadPosterior',
      header: 'Stock resultante',
      align: 'right' as const,
      help: 'Stock del SKU en esa ubicación después del movimiento. Una venta puede dejarlo negativo (se vendió más de lo declarado: revisá con un conteo). '
        + 'En salidas por venta viejas (sin ubicación) es el stock físico total del ERP de entonces.',
      render: (m: MovimientoStock) => (m.cantidadPosterior !== null && m.cantidadPosterior < 0
        ? <span className="text-red-700">{fmtCantidad(m.cantidadPosterior)}</span>
        : fmtCantidad(m.cantidadPosterior)),
    },
    {
      key: 'motivo',
      header: 'Motivo',
      render: (m: MovimientoStock) => (
        <span>
          {m.motivo ?? '—'}
          {m.historico && <span className="block text-xs text-slate-500">{ETIQUETA_SIN_MOTIVO}</span>}
          {m.motivoId != null && m.aCargoDe && (
            <span className="block text-xs text-slate-500">A cargo de: {etiquetaACargoDe(m.aCargoDe)}</span>
          )}
          {datosDelMotivo(m) && <span className="block text-xs text-slate-500">{datosDelMotivo(m)}</span>}
        </span>
      ),
    },
    { key: 'referenciaTexto', header: 'Referencia', render: (m: MovimientoStock) => <ReferenciaMovimiento m={m} /> },
    {
      key: 'operador',
      header: 'Operador',
      help: 'Quién lo hizo. "Sistema": automático o anterior a que se registrara el usuario; "Sistema (Mercado Libre)": despacho de un envío de ML.',
      render: (m: MovimientoStock) => operadorDe(m),
    },
  ];

  // Filas con los textos que muestran las columnas (la exportación a CSV/XLSX toma el valor de cada clave).
  const filas = useFilasConIdentidad(resp?.data ?? [], m => m.skuInterno).map(m => ({
    ...m,
    fechaHecho: fechaHoraAr(momentoDe(m)),
    registrado: fechaHoraAr(m.createdAt),
    operador: operadorDe(m),
    referenciaTexto: referenciaLegible(m.referencia, m.origenReferencia).texto,
  }));

  const totales = resp?.totales ?? { entradas: 0, salidas: 0, neto: 0 };

  const abrirAlta = () => {
    const vacio = formVacio(ubicacionPorDefecto);
    setForm(vacio); setOcurridoInicial(vacio.ocurridoEn); setFormError(''); setCommandId(nuevoCommandId());
    setSkuVerificado(null); verificaciones.current.clear(); limpiarVariantes(); setStockHoy(null); setOpen(true);
  };
  // «Sacar o sumar stock» en Hoy: abre la baja/ajuste y saca ?registrar= para que recargar o Atrás no la reabra.
  // Espera a las ubicaciones para que, con una sola, quede preseleccionada como al tocar el botón.
  const [searchParams, setSearchParams] = useSearchParams();
  const registrarEnUrl = searchParams.get('registrar') === '1';
  const ubicacionesListas = ubicaciones !== null || !!errorUbicaciones;
  useEffect(() => {
    if (!registrarEnUrl || !ubicacionesListas) return;
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('registrar'); return n; }, { replace: true });
    if (puedeRegistrar) abrirAlta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrarEnUrl, ubicacionesListas]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Movimientos de stock</h1>
          <HelpTip
            tips={[
              'Historial de cada entrada y salida de stock: ingresos por compra, salidas por venta, bajas, ajustes, devoluciones y transferencias.',
              'El historial empieza con este deploy: los movimientos anteriores no se registraron.',
              'La fecha es cuándo pasó el movimiento (hora de Argentina). Una baja o un ajuste cargado más tarde puede llevar la fecha y hora en que pasó: el stock cambia al registrarlo, y los conteos toman esa hora para calcular el stock que había.',
              'Los totales de entradas y salidas respetan los filtros y no cuentan las transferencias entre ubicaciones.',
            ]}
          />
          <VerInstructivo seccion="movimientos-de-stock" />
          <p className="text-slate-500 text-sm mt-1">Historial de entradas y salidas por SKU</p>
        </div>
        {vista === 'movimientos' && <Button
          onClick={abrirAlta}
          disabled={!puedeRegistrar}
          title={!puedeRegistrar ? 'No tenés permiso para realizar esta acción' : undefined}
        >
          Registrar baja/ajuste
        </Button>}
        {vista === 'movimientos' && <Button variant="secondary" onClick={abrirReasignar} disabled={!puedeRegistrar}
          title={!puedeRegistrar ? 'No tenés permiso para realizar esta acción' : undefined}>
          Reasignar variante
        </Button>}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200" role="tablist">
        {([['movimientos', 'Movimientos'], ['informe', 'Bajas y altas por motivo']] as Array<[Vista, string]>).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={vista === k} onClick={() => setVista(k)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${vista === k ? 'border-brand-500 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {vista === 'informe' && <BajasAltasPorMotivo />}

      {vista === 'movimientos' && <>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">SKU</label>
            <input
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              placeholder="Buscar SKU"
              value={sku}
              onChange={e => cambiarFiltro(setSku)(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
            <select
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              value={tipo}
              onChange={e => cambiarFiltro(setTipo)(e.target.value)}
            >
              <option value="">Todos</option>
              {(Object.keys(TIPO_LABEL) as TipoMovimiento[]).map(t => (
                <option key={t} value={t}>{TIPO_LABEL[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Desde</label>
            <input type="datetime-local" aria-label="Desde" title="Fecha y hora de Argentina" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={desde} onChange={e => cambiarFiltro(setDesde)(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Hasta</label>
            <input type="datetime-local" aria-label="Hasta" title="Fecha y hora de Argentina (incluye ese minuto)" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={hasta} onChange={e => cambiarFiltro(setHasta)(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Motivo</label>
            <select
              aria-label="Filtrar por motivo"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              value={motivoFiltro}
              onChange={e => cambiarFiltro(setMotivoFiltro)(e.target.value)}
            >
              <option value="">Todos</option>
              <option value={SIN_CATALOGAR} title="Bajas y ajustes manuales registrados antes de que el motivo fuera obligatorio">{ETIQUETA_SIN_MOTIVO}</option>
              {motivos.map(m => (
                <option key={m.id} value={m.id}>{m.nombre}{m.sentido === 'entrada' ? ' (entrada)' : ''}{m.activo ? '' : ' (inactivo)'}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">A cargo de</label>
            <select
              aria-label="Filtrar por a cargo de"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              value={aCargoDeFiltro}
              onChange={e => cambiarFiltro(setACargoDeFiltro)(e.target.value)}
            >
              <option value="">Todos</option>
              {A_CARGO_DE.map(a => <option key={a} value={a}>{A_CARGO_DE_LABEL[a]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Operador</label>
            <select
              aria-label="Filtrar por operador"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              value={operadorFiltro}
              onChange={e => cambiarFiltro(setOperadorFiltro)(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="sistema">Sistema (automáticos)</option>
              {operadores.map(o => <option key={o.id} value={o.id}>{o.username}</option>)}
            </select>
          </div>
        </div>
        {!desde && resp?.desde && (
          <p className="text-xs text-slate-500 mt-2">
            Sin "Desde": se muestran los últimos {VENTANA_MOVIMIENTOS_DIAS} días (desde el {fechaHoraAr(resp.desde)}). Para ver más atrás, elegí la fecha.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Entradas</p>
          <p className="text-2xl font-semibold text-green-700 mt-1">+{fmtCantidad(totales.entradas)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Salidas</p>
          <p className="text-2xl font-semibold text-red-700 mt-1">−{fmtCantidad(totales.salidas)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Neto</p>
          <p className="text-2xl font-semibold text-slate-800 mt-1">{totales.neto > 0 ? '+' : ''}{fmtCantidad(totales.neto)}</p>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <DataTable
        columns={columns}
        data={filas}
        loading={loading}
        emptyMessage="Sin movimientos para los filtros seleccionados"
        title="Movimientos-de-stock"
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={resp?.total ?? 0}
        onChange={setPage}
        onPageSizeChange={s => { setPageSize(s); setPage(1); }}
      />
      </>}

      <Modal open={open} cierre={cierreAlta} title="Registrar baja/ajuste" size="sm">
        <div {...obligatorios.contenedor} className="space-y-4">
          <LeyendaObligatorio />
          <div>
            <label htmlFor={`${idSku}-motivo`} className="block text-sm font-medium mb-1">Motivo<Obligatorio /></label>
            <select
              id={`${idSku}-motivo`}
              aria-label="Motivo"
              {...faltaSi(!motivoElegido)}
              className="w-full px-3 py-2 border rounded-lg"
              value={form.motivoId}
              onChange={e => setForm({ ...form, motivoId: e.target.value })}
            >
              <option value="">Elegí el motivo</option>
              {([['salida', 'Sale del stock'], ['entrada', 'Entra al stock']] as const).map(([sentido, label]) => (
                <optgroup key={sentido} label={label}>
                  {motivosActivos.filter(m => m.sentido === sentido).map(m => (
                    <option key={m.id} value={m.id}>{m.nombre}{m.aCargoDe ? ` · a cargo de ${etiquetaACargoDe(m.aCargoDe)}` : ''}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {errorMotivos && (
              <AvisoCarga mensaje={`No se pudieron cargar los motivos: ${errorMotivos}`} onReintentar={recargarMotivos} />
            )}
            {sinMotivosActivos && (
              <p className="text-amber-700 text-xs mt-1" role="alert">No hay motivos activos: agregalos en Configuración → Mantenimiento</p>
            )}
            {motivoElegido && (
              <p className="text-xs text-slate-500 mt-1">
                Tipo: <span className="font-medium">{TIPO_LABEL[motivoElegido.tipo]}</span>
                {motivoElegido.aCargoDe && <> · a cargo de {etiquetaACargoDe(motivoElegido.aCargoDe)}</>}
                {' · '}{TIPOS_ENTRADA_MANUAL.includes(motivoElegido.tipo) ? 'suma al stock de la ubicación' : 'descuenta del stock de la ubicación; no puede quedar negativo'}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{pideDetalle ? <>Detalle<Obligatorio /></> : 'Detalle (opcional)'}</label>
            <textarea
              aria-label="Detalle"
              {...faltaSi(!form.detalle.trim(), pideDetalle)}
              rows={2}
              value={form.detalle}
              onChange={e => setForm({ ...form, detalle: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ej.: caja golpeada en el depósito"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {pideReferencia ? <>Referencia<Obligatorio /></> : 'Referencia (opcional)'}
            </label>
            <input
              aria-label="Referencia"
              {...faltaSi(!form.referencia.trim(), pideReferencia)}
              value={form.referencia}
              onChange={e => setForm({ ...form, referencia: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ej.: OV #120, envío, remito del proveedor"
            />
          </div>
          {camposPedidos.map(d => (
            <div key={d.campo}>
              <label className="block text-sm font-medium mb-1">{d.nombre}<Obligatorio /></label>
              <input
                aria-label={d.nombre}
                {...faltaSi(!form[d.campo].trim())}
                value={form[d.campo]}
                onChange={e => setForm({ ...form, [d.campo]: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={d.ejemplo}
              />
            </div>
          ))}
          <div>
            <label htmlFor={idSku} className="block text-sm font-medium mb-1">SKU<Obligatorio /></label>
            <BuscadorSku
              id={idSku}
              aria-label="SKU"
              placeholder="Escribí el SKU o la descripción"
              {...faltaSi(!form.skuInterno.trim() || !!errorSku)}
              aria-invalid={!!errorSku || undefined}
              aria-describedby={errorSku ? `${idSku}-error` : undefined}
              value={form.skuInterno}
              mostrarCodigo
              mostrarProveedor
              onChange={texto => { setForm(f => ({ ...f, skuInterno: texto })); if (texto.trim() !== skuVariantes.current) limpiarVariantes(); }}
              onElegir={elegirSku}
              onBlur={() => { void verificarSku(form.skuInterno.trim()); }}
              buscar={buscarSkus}
            />
            {errorSku && <p id={`${idSku}-error`} className="text-red-600 text-xs mt-1">{errorSku}</p>}
          </div>
          {variantes && (
            <div>
              <span className="block text-sm font-medium mb-1">Variante</span>
              <ChipsVariante etiqueta="Variante" variantes={variantes.variantes} valor={varianteId} onChange={setVarianteId} />
              <p className="text-xs text-slate-500 mt-1">Dejá «Sin asignar» si la baja o el alta no es de una variante en particular.</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Cantidad<Obligatorio /></label>
            <input
              type="number"
              aria-label="Cantidad"
              {...faltaSi(!(Number(form.cantidad) > 0))}
              min="0"
              step="any"
              value={form.cantidad}
              onChange={e => setForm({ ...form, cantidad: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label htmlFor={`${idSku}-ocurrido`} className="block text-sm font-medium mb-1">Fecha y hora del hecho</label>
            <input
              id={`${idSku}-ocurrido`}
              type="datetime-local"
              aria-label="Fecha y hora del hecho"
              value={form.ocurridoEn}
              min={minHechoLocalAr()}
              max={ahoraLocalAr()}
              onChange={e => setForm({ ...form, ocurridoEn: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
            <p className="text-xs text-slate-500 mt-1">
              Cuándo pasó (hora de Argentina), hasta {MAX_DIAS_ATRAS_HECHO} días atrás. El stock cambia al registrarlo; los conteos usan esta hora.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ubicación<Obligatorio /></label>
            <select
              aria-label="Ubicación"
              required
              className="w-full px-3 py-2 border rounded-lg"
              value={form.ubicacionId}
              onChange={e => setForm({ ...form, ubicacionId: e.target.value })}
            >
              <option value="">Elegí una ubicación</option>
              {listaUbicaciones.map(u => (
                <option key={u.id} value={u.id}>{u.codigo}</option>
              ))}
            </select>
            {errorUbicaciones && (
              <AvisoCarga mensaje={`No se pudieron cargar las ubicaciones: ${errorUbicaciones}`} onReintentar={recargarUbicaciones} />
            )}
          </div>
          {stockActual && (
            <p role="status" className={`rounded-lg px-3 py-2 text-sm tabular-nums ${quedara !== null && quedara < 0 ? 'bg-red-50 text-red-700 font-medium' : 'bg-slate-50 text-slate-700'}`}>
              {stockActual.cantidad === null
                ? 'No se pudo ver el stock de hoy.'
                : noAlcanza
                  ? `No alcanza: hay ${fmtCantidad(stockActual.cantidad)} en esta ubicación, no se pueden sacar ${fmtCantidad(cantidadNum)}.`
                  : quedara === null
                  ? `Stock hoy ${fmtCantidad(stockActual.cantidad)}`
                  : `Stock hoy ${fmtCantidad(stockActual.cantidad)} → quedará ${fmtCantidad(quedara)} (${sale ? 'sale' : 'entra'})${quedara < 0 ? ' — va a quedar negativo' : ''}`}
            </p>
          )}
          {formError && <p className="text-red-600 text-sm">{formError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={cierreAlta.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
            <Button onClick={guardar} loading={submitting} disabled={!puedeRegistrar || noSePuedeRegistrar || noAlcanza}>
              {submitting ? 'Guardando...' : textoBoton}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={reasignando} onClose={() => setReasignando(false)} title="Reasignar variante" size="sm">
        <div className="space-y-4">
          <p className="text-xs text-slate-500">Mueve unidades entre variantes del mismo SKU, o desde y hacia «Sin asignar». El total del SKU no cambia.</p>
          <div>
            <label htmlFor={`${idSku}-re-sku`} className="block text-sm font-medium mb-1">SKU</label>
            <BuscadorSku
              id={`${idSku}-re-sku`}
              aria-label="SKU"
              placeholder="Escribí el SKU o la descripción"
              value={reSku}
              mostrarCodigo
              mostrarProveedor
              onChange={texto => { setReSku(texto); if (texto.trim() !== reSkuElegido.current) { reSkuElegido.current = ''; setReVariantes(null); setReDesde(null); setReHacia(null); } }}
              onElegir={elegirSkuReasignar}
              buscar={buscarSkus}
              resolver={resolverSkus}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cantidad</label>
            <input type="number" aria-label="Cantidad" min="0" step="any" value={reCantidad}
              onChange={e => setReCantidad(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          {reVariantes && (<>
            <div>
              <span className="block text-sm font-medium mb-1">Desde</span>
              <ChipsVariante etiqueta="Desde" variantes={reVariantes.variantes} sinAsignar={reVariantes.sinAsignar} valor={reDesde} onChange={setReDesde} />
            </div>
            <div>
              <span className="block text-sm font-medium mb-1">Hacia</span>
              <ChipsVariante etiqueta="Hacia" variantes={reVariantes.variantes} sinAsignar={reVariantes.sinAsignar} valor={reHacia} onChange={setReHacia} />
            </div>
          </>)}
          {reError && <p className="text-red-600 text-sm">{reError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setReasignando(false)} className="px-4 py-2 text-sm">Cancelar</button>
            <Button onClick={reasignar} loading={reEnviando} disabled={reEnviando || !reVariantes || !(Number(reCantidad) > 0)}>
              {reEnviando ? 'Guardando...' : 'Reasignar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
